import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useDeleteTenantOverride from 'Hooks/query-hooks/tenant/useDeleteTenantOverride';
import useTenantErrorHandler from 'Hooks/query-hooks/tenant/useTenantErrorHandler';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { getUser } from 'Selectors';
import styles from './DeleteTenantOverrideModal.css';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    id: string;
};

const DeleteTenantOverrideModal = ({ closeModal, isOpen, id }: Props) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const errorHandler = useTenantErrorHandler();
    const { tenantKey, landlordId } = useSelector(getUser);
    const isImpersonationMode = Boolean(landlordId) && landlordId !== tenantKey;
    const onSuccess = () => {
        addAlert({
            id: 'remove-rule',
            content: t('Tenant Override removed', { id }),
            status: 'success'
        });
        closeModal();
    };

    const deleteMutation = useDeleteTenantOverride({
        ruleId: id,
        mutationOptions: { onError: errorHandler, onSuccess }
    });

    const handleTrackEventClick = () => {
        let clickIdentifier = 'Global Rules - Remove Tenant Override';
        let text = 'Remove Tenant Override';
        if (isImpersonationMode) {
            clickIdentifier = 'Advanced Tenant Rule - Remove Override';
            text = 'Remove Advanced Tenant Rule Override';
        }
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            containerClass={styles.modalContainer}
            status='warning'
            dataTestId='delete-tenant-override-modal'
        >
            <Modal.Title
                id='remove-tenant-override-modal-title'
                title={t('Remove this Tenant Override?')}
                onClose={closeModal}
                showCloseIcon={false}
                statusIcon='warning'
            />
            <div className={styles.deleteWarningContent}>
                <div className={styles.tenantInfo}>
                    {t('Remove tenant override warning', {
                        id
                    })}
                </div>
                {t('This cannot be undone')}.
            </div>
            <Modal.Footer
                id='remove-tenant-override-modal-footer'
                onActionText='Remove Override'
                disableOnAction={deleteMutation?.isLoading}
                onClose={closeModal}
                onAction={() => {
                    closeModal();
                    deleteMutation.mutate(id);
                    handleTrackEventClick();
                }}
            />
        </Modal>
    );
};

export default DeleteTenantOverrideModal;
